import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { Box } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory, useParams } from 'react-router-dom'
import { Form as FinalForm } from 'react-final-form'
import arrayMutators from 'final-form-arrays'

import AgendaDetailForm from './AgendaDetailForm'
import MeetingParticipantsForm from './MeetingParticipantsForm'
import MeetingAgendaItemsForm from './MeetingAgendaItemsForm'
import MyButton from '../../../control/button/button'

import useLoading from '../../../control/loading/useLoading'
import { useSettingAgendas } from '../../../services/um/useSettingAgendas'

import { buildAgendaPayload, formatYmd, hasAtLeastOneAttendance } from './utils'
import { mapAgendaResponseToForm } from './utils'
import type { AgendaEditorForm } from './agendaTypes'
import { downloadAgendaDocx } from './utils/exportAgendaDocx'
import { downloadAgendaPdf } from './utils/exportAgendaPdf'
import useTranslate from '../../../i18n/useTranslate'
import Dialog, { DialogType } from '../../../control/dialog/dialog'
import ApproveAgendaForm, { ApprovalStatusId } from './ApproveAgendaForm'
import SecretCodeForm from '../../web/shareUI/secretCodeForm/secretCodeForm'
import { encryption } from '../../../utilities/encryption'
import useAuthenStore from '../../../stores/authen/authenStore'
import userGroup from '../../../constants/userGroup'
import { saveAs } from 'file-saver'

const ALLOWED_SIGN_GROUPS = new Set<number>([userGroup.secretary, userGroup.officer])

const EditAgendaTab = () => {
    const classes = useStyles()
    const loading = useLoading()
    const history = useHistory()
    const t = useTranslate()
    const profileStore = useAuthenStore()

    const { getAgendaById, upsertAgendas, sendMail, approveAgenda, getAgendaFileArrayBuffer } = useSettingAgendas()
    const { id } = useParams<{ id: string }>()

    const [initialValues, setInitialValues] = useState<Partial<AgendaEditorForm> | null>(null)
    const [apiAgendaData, setApiAgendaData] = useState<any | null>(null)
    const [rev, setRev] = useState(0)

    const submitIntentRef = useRef<'draft' | 'submit'>('draft')

    const [configDialog, setConfigDialog] =
        useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()
    const dialogRef = useRef<Record<string, Function>>()
    const onRefDialog = (fn: Record<string, Function>) => {
        dialogRef.current = fn
    }

    useEffect(() => {
        let alive = true
        ;(async () => {
            loading.loadingOpen()
            try {
                const res = await getAgendaById(String(profileStore?.profile?.irb), id)
                const data = (res?.result ?? res?.data ?? res) as any
                const mappedForm = mapAgendaResponseToForm(data)
                if (alive) {
                    setInitialValues(mappedForm)
                    setApiAgendaData(data)
                }
            } finally {
                loading.loadingClose()
            }
        })()
        return () => {
            alive = false
        }
    }, [id])

    const refreshAgenda = async () => {
        const res = await getAgendaById(String(profileStore?.profile?.irb), id)
        const data = (res?.result ?? res?.data ?? res) as any
        const mappedForm = mapAgendaResponseToForm(data)
        setInitialValues(mappedForm)
        setApiAgendaData(data)
        setRev((r) => r + 1)
    }

    const formKey = useMemo(() => `init-${id}-${rev}`, [id, rev])

    const confirmFormSubmit = (intent: 'draft' | 'submit', form: any) => {

        if (intent === 'draft') {
            submitIntentRef.current = 'draft'
            void onSubmit(form.getState().values)
            return
        }

        if (!hasAtLeastOneAttendance(form.getState().values)) {
            setConfigDialog({
                title: t.get('ALERT.TITLE.NOTIFICATION'),
                content: t.get('UM.SETTING_AGENDA.PARTICIPANTS.REQUIRE_ATTENDANCE'),
                type: 'alert-message',
            })
            dialogRef.current?.openDialog()
            return
        }

        setConfigDialog({
            title: t.get('ALERT.TITLE.NOTIFICATION'),
            content: t.get('ALERT.CONTENT.NEED_TO_SAVE'),
            type: 'alert-confirm',
            onClickConfirm: () => {
                dialogRef.current?.closeDialog()
                submitIntentRef.current = 'submit'
                form.submit()
            },
            onAfterClose: () => setConfigDialog(undefined),
        })
        dialogRef.current?.openDialog()
    }

    const onSubmit = async (values: any) => {
        loading.loadingOpen()
        try {
            const isDraft = submitIntentRef.current === 'draft'
            const payload = { ...buildAgendaPayload(values), agendaId: Number(id), isDraft }
            const res = await upsertAgendas(String(profileStore?.profile?.irb), payload)
            if (res?.message === 'duplicated agenda_no') {
                setConfigDialog({
                    title: t.get('ALERT.TITLE.NOTIFICATION'),
                    content: t.get('ALERT.CONTENT.DUPLICATED_AGENDA_NO'),
                    type: 'alert-message',
                })
                dialogRef.current?.openDialog?.()
                return
            }
            await refreshAgenda()
            setConfigDialog({
                title: t.get('ALERT.TITLE.NOTIFICATION'),
                content: t.get('ALERT.CONTENT.SAVE_SUCCESS'),
                type: 'alert-message',
            })
            dialogRef.current?.openDialog()
        } finally {
            loading.loadingClose()
        }
    }

    const handleExportWord = async () => {
        if (!apiAgendaData) return
        await downloadAgendaDocx(apiAgendaData)
    }

    const handleExportPDF = async () => {
        if (!apiAgendaData) return
        await downloadAgendaPdf(apiAgendaData)
    }

    const onConfirmSendEmail = async () => {
        if (!id) return
        loading.loadingOpen()
        try {
            await sendMail(String(profileStore?.profile?.irb), id)
            setConfigDialog({
                title: t.get('ALERT.TITLE.NOTIFICATION'),
                content: t.get('ALERT.CONTENT.SEND_EMAIL_SUCCESS'),
                type: 'alert-message',
            })
        } finally {
            loading.loadingClose()
            dialogRef.current?.openDialog()
        }
    }

    const handleSendEmail = () => {
        if (!id) return
        setConfigDialog({
            title: t.get('ALERT.TITLE.NOTIFICATION'),
            content: t.get('ALERT.CONTENT.NEED_TO_SEND_EMAIL'),
            type: 'alert-confirm',
            onClickConfirm: () => {
                dialogRef.current?.closeDialog()
                void onConfirmSendEmail()
            },
            onAfterClose: () => setConfigDialog(undefined),
        })
        dialogRef.current?.openDialog()
    }

    const onConfirmApproveAgenda = async (values: any, plainPin?: string) => {
        if (!id) return
        loading.loadingOpen()
        try {
            const status = String(values?.approvalStatusId)
            const isApprove = status === ApprovalStatusId.approved

            const payload = {
                isApprove,
                ...(isApprove && plainPin
                    ? { signUserPinCode: encryption(plainPin), signUserID: String(profileStore.profile.userId) }
                    : {}),
                ...(!isApprove && (values?.remark ?? '').trim() ? { remark: (values.remark as string).trim() } : {}),
            }

            const res = await approveAgenda(String(profileStore?.profile?.irb), id, payload)
            if (res.status === 1) {
                await refreshAgenda()
                setConfigDialog({
                    title: t.get('ALERT.TITLE.NOTIFICATION'),
                    content: t.get('ALERT.CONTENT.SAVE_SUCCESS'),
                    type: 'alert-message',
                })
            } else {
                setConfigDialog({
                    title: t.get('ALERT.TITLE.NOTIFICATION'),
                    content: t.get('ALERT.CONTENT.SECRET_PASSWORD_INCORRECT'),
                    type: 'alert-message',
                })
            }
        } finally {
            loading.loadingClose()
            dialogRef.current?.openDialog()
        }
    }

    const handleApproveAgenda = (values: any) => {
        const isApprove = values.approvalStatusId === ApprovalStatusId.approved

        if (!isApprove) {
            setConfigDialog({
                title: t.get('ALERT.TITLE.NOTIFICATION'),
                content: t.get('ALERT.CONTENT.NEED_TO_SAVE'),
                type: 'alert-confirm',
                onClickConfirm: () => {
                    dialogRef.current?.closeDialog()
                    setTimeout(() => onConfirmApproveAgenda(values), 0)
                },
                onAfterClose: () => setConfigDialog(undefined),
            })
            dialogRef.current?.openDialog()
            return
        }

        setConfigDialog({
            title: t.get('LABEL.SECRET_PASSWORD'),
            content: (
                <SecretCodeForm
                    onClickClose={() => dialogRef.current?.closeDialog()}
                    onClickSave={(val: any) => {
                        dialogRef.current?.closeDialog()
                        onConfirmApproveAgenda(values, val.password)
                    }}
                />
            ),
            type: '',
        })
        dialogRef.current?.openDialog()
    }

    const isTh = useMemo(() => t.languageStore.currentLanguage === t.languageKey.th, [t.languageStore.currentLanguage])

    const handleExportMemo = async () => {
        if (!id) return
        loading.loadingOpen()
        try {
            const irb = String(profileStore?.profile?.irb)
            const fileName = `${`${t.get('UM.SETTING_AGENDA.DOCUMENT.MEMORANDUM')}_${formatYmd(new Date())}`}`
            const keyName = isTh ? 'unSignFileName_th' : 'unSignFileName_en'
            const res: ArrayBuffer = await getAgendaFileArrayBuffer(irb, id, keyName, fileName)
            const blob = new Blob([res], { type: 'application/pdf' })
            saveAs(blob, fileName)
        } catch (err) {
            console.error(err)
        } finally {
            loading.loadingClose()
        }
    }

    const handleExportMemoSigned = async () => {
        if (!id) return
        loading.loadingOpen()
        try {
            const irb = String(profileStore?.profile?.irb)
            const fileName = `${`${t.get('UM.SETTING_AGENDA.DOCUMENT.MEMORANDUM_SIGNED')}_${formatYmd(new Date())}`}`
            const keyName = isTh ? 'signFileName_th' : 'signFileName_en'
            const res: ArrayBuffer = await getAgendaFileArrayBuffer(irb, id, keyName, fileName)
            const blob = new Blob([res], { type: 'application/pdf' })
            saveAs(blob, fileName)
        } catch (err) {
            console.error(err)
        } finally {
            loading.loadingClose()
        }
    }

    if (!initialValues) return null

    const canSign = Number(initialValues?.submittedStatusId) === 2
    const canEdit = [1, 11].includes(apiAgendaData?.submittedStatusId)
    const userGroupId = profileStore?.profile?.userGroupId ?? 0
    const canSignByRole = ALLOWED_SIGN_GROUPS.has(userGroupId)
    const isSignDisabled = !canSignByRole
    const canRejected = [11].includes(apiAgendaData?.submittedStatusId)
    const statusId = apiAgendaData?.submittedStatusId

    const fileVisibility = {
        showMemo: true,
        showMemoSigned: true,
        showWord: true,
        showPDF: true,
    }

    switch (statusId) {
        case 1:
            fileVisibility.showMemoSigned = false
            break

        case 2:
            fileVisibility.showMemo = false
            fileVisibility.showMemoSigned = false
            break

        case 8:
            // แสดงทั้งหมด (default true อยู่แล้ว)
            break

        case 11:
            fileVisibility.showMemoSigned = false
            break

        default:
            break
    }

    // In edit mode, always show memo export icon (including saved draft state).
    fileVisibility.showMemo = true

    return (
        <>
            <FinalForm
                key={formKey}
                initialValues={initialValues}
                mutators={{ ...arrayMutators }}
                onSubmit={onSubmit}
                validate={() => ({})}
                render={({ form }) => (
                    <Box component="form" display="flex" flexDirection="column" style={{ gap: 20 }}>
                        <AgendaDetailForm
                            canEdit={canEdit}
                            onExportMemo={handleExportMemo}
                            onExportMemoSigned={handleExportMemoSigned}
                            onExportWord={handleExportWord}
                            onExportPDF={handleExportPDF}
                            showExportButton
                            fileVisibility={fileVisibility}
                        />
                        <MeetingParticipantsForm prefix="participants" canEdit={canEdit} />
                        <MeetingAgendaItemsForm prefix="topics" canEdit={canEdit} />

                        {canRejected && <ApproveAgendaForm canEdit={false} />}

                        {!canSign && (
                            <Box className={classes.pageFooter}>
                                {canEdit ? (
                                    <>
                                        <MyButton
                                            className={classes.footerButton}
                                            variant="contained"
                                            onClick={() => confirmFormSubmit('draft', form)}
                                        >
                                            {t.get('UM.SETTING_AGENDA.BUTTON.SAVE_DRAFT')}
                                        </MyButton>

                                        <div className={classes.rightActions}>
                                            <MyButton
                                                className={classes.footerButton}
                                                variant="outlined"
                                                onClick={() => {
                                                    history.push('/settingAgendas')
                                                }}
                                            >
                                                {t.get('UM.SETTING_AGENDA.BUTTON.CANCEL')}
                                            </MyButton>

                                            <MyButton
                                                className={classes.footerButton}
                                                variant="contained"
                                                color="primary"
                                                onClick={() => confirmFormSubmit('submit', form)}
                                            >
                                                {t.get('UM.SETTING_AGENDA.BUTTON.SAVE_AND_REQUEST_APPROVAL')}
                                            </MyButton>
                                        </div>
                                    </>
                                ) : (
                                    <div className={classes.rightActions}>
                                        <MyButton
                                            className={classes.footerButton}
                                            variant="outlined"
                                            onClick={() => {
                                                history.push('/settingAgendas')
                                            }}
                                        >
                                            {t.get('UM.SETTING_AGENDA.BUTTON.CANCEL')}
                                        </MyButton>
                                        <MyButton
                                            className={clsx(classes.footerButton, classes.sendEmailButton)}
                                            variant="contained"
                                            onClick={handleSendEmail}
                                        >
                                            {t.get('UM.SETTING_AGENDA.BUTTON.SEND_EMAIL')}
                                        </MyButton>
                                    </div>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            />

            {canSign && (
                <FinalForm
                    key={`sign-form-${id}-${rev}`}
                    initialValues={initialValues}
                    mutators={{ ...arrayMutators }}
                    onSubmit={handleApproveAgenda}
                    validate={() => ({})}
                    render={({ handleSubmit }) => (
                        <>
                            <ApproveAgendaForm />
                            <Box className={classes.pageFooter}>
                                <div className={classes.rightActions}>
                                    <MyButton
                                        className={classes.footerButton}
                                        variant="outlined"
                                        onClick={() => {
                                            history.push('/settingAgendas')
                                        }}
                                    >
                                        {t.get('UM.SETTING_AGENDA.BUTTON.CANCEL')}
                                    </MyButton>
                                    <MyButton
                                        variant="contained"
                                        color="primary"
                                        disabled={isSignDisabled}
                                        onClick={handleSubmit}
                                    >
                                        {t.get('UM.SETTING_AGENDA.BUTTON.SIGN')}
                                    </MyButton>
                                </div>
                            </Box>
                        </>
                    )}
                />
            )}

            <Dialog onRef={onRefDialog} {...configDialog} />
        </>
    )
}

const useStyles = makeStyles((theme) => ({
    sendEmailButton: {
        backgroundColor: '#0062FF',
        color: '#fff',
        transition: 'background-color .2s ease, box-shadow .2s ease',
        '&&:hover': { backgroundColor: '#004DE6', boxShadow: '0 2px 8px rgba(0,0,0,.15)' },
        '&&:active': { backgroundColor: '#003FC7', boxShadow: '0 1px 4px rgba(0,0,0,.12)' },
        '&&.Mui-disabled': { backgroundColor: '#B3D1FF', color: '#fff' },
    },
    pageFooter: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    rightActions: {
        marginLeft: 'auto',
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            width: '100%',
            flexDirection: 'column',
        },
    },
    footerButton: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
}))

export default EditAgendaTab
